import React, { useState, useEffect } from 'react';
import { ref, push, onValue, update, remove } from 'firebase/database';
import { db, storage } from '../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

const AddTestimonials = () => {
  const [form, setForm] = useState({
    name: '',
    role: '',
    message: '',
    rating: 5,
    imageUrl: '',
    uploadMethod: 'file',
    imageFile: null
  });

  const [testimonials, setTestimonials] = useState([]);
  const [editId, setEditId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const testimonialsRef = ref(db, 'testimonials');
    const unsubscribe = onValue(testimonialsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const items = Object.entries(data).map(([id, value]) => ({ id, ...value }));
        setTestimonials(items);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleFileChange = (e) => {
    setForm(prev => ({
      ...prev,
      imageFile: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      let imageUrl = form.imageUrl;

      // Validate required fields
      if (!form.name || !form.role || !form.message) {
        throw new Error('Please fill in all required fields');
      }

      // Handle file upload if method is file and file is selected
      if (form.uploadMethod === 'file' && form.imageFile) {
        const fileRef = storageRef(storage, `testimonials/${uuidv4()}`);
        await uploadBytes(fileRef, form.imageFile);
        imageUrl = await getDownloadURL(fileRef);
      } else if (form.uploadMethod === 'file' && !form.imageFile && !editId) {
        throw new Error('Please select an image file');
      } else if (form.uploadMethod === 'url' && !form.imageUrl && !editId) {
        throw new Error('Please provide an image URL');
      }

      const testimonialData = {
        name: form.name,
        role: form.role,
        message: form.message,
        rating: Number(form.rating),
        imageUrl: imageUrl || (editId ? testimonials.find(item => item.id === editId)?.imageUrl : ''),
        createdAt: editId ? testimonials.find(item => item.id === editId)?.createdAt : new Date().toISOString()
      };

      if (editId) {
        // Update existing testimonial
        await update(ref(db, `testimonials/${editId}`), testimonialData);
        setSuccessMessage('Testimonial updated successfully!');
      } else {
        // Add new testimonial
        await push(ref(db, 'testimonials'), testimonialData);
        setSuccessMessage('Testimonial added successfully!');
      }

      // Reset form
      setForm({
        name: '',
        role: '',
        message: '',
        rating: 5,
        imageUrl: '',
        uploadMethod: 'file',
        imageFile: null
      });
      setEditId(null);
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = (testimonial) => {
    setForm({
      name: testimonial.name,
      role: testimonial.role,
      message: testimonial.message,
      rating: testimonial.rating,
      imageUrl: testimonial.imageUrl,
      uploadMethod: testimonial.imageUrl.startsWith('http') ? 'url' : 'file',
      imageFile: null
    });
    setEditId(testimonial.id);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        const testimonial = testimonials.find(item => item.id === id);
        
        // Delete from database
        await remove(ref(db, `testimonials/${id}`));

        // If the image is stored in Firebase Storage, delete it
        if (testimonial.imageUrl.includes('firebasestorage.googleapis.com')) {
          const fileRef = storageRef(storage, testimonial.imageUrl);
          await deleteObject(fileRef).catch(error => {
            console.log('Error deleting storage file:', error);
          });
        }

        setSuccessMessage('Testimonial deleted successfully!');
      } catch (error) {
        console.error('Error deleting testimonial:', error);
        setErrorMessage('Failed to delete testimonial. Please try again.');
      }
    }
  };

  const cancelEdit = () => {
    setForm({
      name: '',
      role: '',
      message: '',
      rating: 5,
      imageUrl: '',
      uploadMethod: 'file',
      imageFile: null
    });
    setEditId(null);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>{editId ? 'Edit' : 'Add'} Testimonial ★</h2>
      
      {successMessage && <div style={styles.successMessage}>{successMessage}</div>}
      {errorMessage && <div style={styles.errorMessage}>{errorMessage}</div>}

      <form onSubmit={handleSubmit} style={styles.form}>
        <input
          name="name"
          placeholder="Name*"
          value={form.name}
          onChange={handleChange}
          style={styles.input}
          required
        />

        <input
          name="role"
          placeholder="Role*"
          value={form.role}
          onChange={handleChange}
          style={styles.input}
          required
        />

        <textarea
          name="message"
          placeholder="Message*"
          value={form.message}
          onChange={handleChange}
          style={{ ...styles.input, height: '100px' }}
          required
        />

        <select
          name="rating"
          value={form.rating}
          onChange={handleChange}
          style={styles.input}
          required
        >
          {[5, 4, 3, 2, 1].map((num) => (
            <option key={num} value={num}>
              {'★'.repeat(num)}{'☆'.repeat(5 - num)}
            </option>
          ))}
        </select>

        <div style={styles.uploadMethod}>
          <label style={styles.label}>Upload Method</label>
          <div style={styles.radioGroup}>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                name="uploadMethod"
                value="file"
                checked={form.uploadMethod === 'file'}
                onChange={handleChange}
              />
              Upload File
            </label>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                name="uploadMethod"
                value="url"
                checked={form.uploadMethod === 'url'}
                onChange={handleChange}
              />
              Image URL
            </label>
          </div>
        </div>

        {form.uploadMethod === 'file' ? (
          <div>
            <label style={styles.label}>Profile Image {editId && '(Leave empty to keep current image)'}</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={styles.input}
              required={!editId}
            />
          </div>
        ) : (
          <input
            name="imageUrl"
            placeholder="Image URL*"
            value={form.imageUrl}
            onChange={handleChange}
            style={styles.input}
            required={!editId}
          />
        )}

        <div style={styles.buttonGroup}>
          <button 
            type="submit" 
            style={styles.submitButton}
            disabled={isUploading}
          >
            {isUploading ? 'Processing...' : (editId ? 'Update Testimonial' : 'Add Testimonial')}
          </button>
          
          {editId && (
            <button 
              type="button" 
              onClick={cancelEdit}
              style={styles.cancelButton}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <hr style={styles.divider} />

      <h3 style={styles.subHeader}>All Testimonials</h3>
      
      {testimonials.length === 0 ? (
        <p style={styles.noItems}>No testimonials found. Add some using the form above.</p>
      ) : (
        <div style={styles.grid}>
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <img 
                  src={testimonial.imageUrl} 
                  alt={testimonial.name}
                  style={styles.cardImage}
                />
                <div>
                  <h4 style={styles.cardTitle}>{testimonial.name}</h4>
                  <p style={styles.cardRole}>{testimonial.role}</p>
                </div>
              </div>
              <div style={styles.cardRating}>
                {'★'.repeat(testimonial.rating)}{'☆'.repeat(5 - testimonial.rating)}
              </div>
              <p style={styles.cardMessage}>{testimonial.message}</p>
              <div style={styles.btnGroup}>
                <button 
                  onClick={() => handleEdit(testimonial)} 
                  style={styles.editBtn}
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDelete(testimonial.id)} 
                  style={styles.deleteBtn}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1000px',
    margin: '40px auto',
    padding: '20px',
    fontFamily: 'Segoe UI, sans-serif'
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
    color: '#333'
  },
  subHeader: {
    marginBottom: '20px',
    color: '#333'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    background: '#f0f8ff',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  },
  uploadMethod: {
    marginBottom: '10px'
  },
  radioGroup: {
    display: 'flex',
    gap: '15px',
    marginTop: '5px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  },
  label: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: '500',
    color: '#333'
  },
  input: {
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    fontSize: '16px',
    width: '100%'
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    marginTop: '10px'
  },
  submitButton: {
    padding: '12px',
    backgroundColor: '#0077b6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    flex: 1
  },
  cancelButton: {
    padding: '12px',
    backgroundColor: '#e63946',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    flex: 1
  },
  divider: {
    margin: '40px 0',
    border: 'none',
    borderTop: '1px solid #ddd'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '20px'
  },
  card: {
    background: '#ffffff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  cardImage: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    objectFit: 'cover'
  },
  cardTitle: {
    margin: 0,
    fontSize: '18px',
    color: '#333'
  },
  cardRole: {
    margin: 0,
    color: '#666',
    fontSize: '14px'
  },
  cardRating: {
    color: '#ffc107',
    fontSize: '18px'
  },
  cardMessage: {
    margin: 0,
    color: '#555',
    lineHeight: '1.5'
  },
  btnGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '15px'
  },
  editBtn: {
    backgroundColor: '#007bff',
    border: 'none',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  deleteBtn: {
    backgroundColor: '#dc3545',
    border: 'none',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  successMessage: {
    padding: '10px',
    backgroundColor: '#d4edda',
    color: '#155724',
    borderRadius: '4px',
    marginBottom: '20px',
    textAlign: 'center'
  },
  errorMessage: {
    padding: '10px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '4px',
    marginBottom: '20px',
    textAlign: 'center'
  },
  noItems: {
    textAlign: 'center',
    color: '#777',
    padding: '20px'
  }
};

export default AddTestimonials;